import {resendVerificationEmailOptions} from '@common/auth/auth-queries';
import {
  maskEmailAddress,
  needsEmailVerification,
  verifyEmailUrl,
} from '@common/auth/email-verification-state';
import {useAuth} from '@common/auth/use-auth';
import {showHttpErrorToast} from '@common/http/errors/show-http-error-toast';
import {Button, LinkButton} from '@shadcn/button/button';
import {toast} from '@shadcn/toast/toast';
import {useMutation} from '@tanstack/react-query';
import {Trans} from '@ui/i18n/trans';
import {MailWarningIcon} from 'lucide-react';
import {useLocation} from 'react-router';

const HIDDEN_ON = /^\/(verify-email|login|register|logout|admin)(\/|$)/;

/**
 * Shown on the pages an unconfirmed person can open (pricing, account
 * settings, notifications…) so they always know what is missing.
 */
export function EmailVerificationBanner() {
  const {user} = useAuth();
  const location = useLocation();
  const resend = useMutation(resendVerificationEmailOptions());

  if (!user || !needsEmailVerification() || HIDDEN_ON.test(location.pathname)) {
    return null;
  }

  return (
    <div
      role="status"
      data-testid="email-verification-banner"
      className="sticky top-0 z-40 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-primary/25 bg-primary/10 px-4 py-2.5 text-sm text-foreground"
    >
      <MailWarningIcon className="size-4 shrink-0 text-primary" />
      <p className="min-w-0 flex-auto">
        <span className="font-semibold">
          <Trans message="Confirm your email to start using Cloud Drive." />
        </span>{' '}
        <span className="text-muted-foreground">
          <Trans
            message="We sent a code to :email."
            values={{email: maskEmailAddress(user.email)}}
          />
        </span>
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <LinkButton
          size="sm"
          to={verifyEmailUrl(`${location.pathname}${location.search}`)}
        >
          <Trans message="Enter code" />
        </LinkButton>
        <Button
          size="sm"
          variant="outline"
          className="bg-background"
          disabled={resend.isPending}
          onClick={() =>
            resend.mutate(
              {},
              {
                onSuccess: () =>
                  toast.success(
                    <Trans
                      message="We sent a new code to :email"
                      values={{email: maskEmailAddress(user.email)}}
                    />,
                  ),
                onError: err => showHttpErrorToast(err),
              },
            )
          }
        >
          <Trans message="Resend" />
        </Button>
      </div>
    </div>
  );
}
